import {Box} from '@mui/material';
import UserAppliedTag, {IUserAppliedTagList} from './UserAppliedTag';
import SelectFieldAndApplyTag from './SelectFieldAndApplyTag';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useGetEcaFields} from '../../../../hooks/eca-tagging/useGetEcaFields';
import ExportTag from './ExportTag';
import {tagFieldmockData} from '../../../../data/tagFieldMockData';
import {IFieldModal} from '../../../pages/field-management/custom-field/constant';
import {ITag} from '../../../pages/field-management/custom-field/DraggableTag';
import AurLoadingIndicator from '../../../common/aur-loading-indicator/AurLoadingIndictor';
import {useGetSelectedTags} from '../../../../hooks/eca-tagging/useGetSelectedTags';
import AurDataGridErrorOverlay from '../../../common/aur-data-grid-error-overlay/AurDataGridErrorOverlay';
import {useLocation} from 'react-router-dom';
import {getIndexByClientIdAndProjectId} from '../../../../api/EcaServices';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';

export interface ITaggingPannelProps {
  activeDocumentId: string;
  searchId: number;
  indexName: string;
  stdIndexFieldTemplateId?: number;
  isExpanded?: boolean;
}

interface LocalUserAppliedTagsState {
  scopeKey: string;
  tags: IUserAppliedTagList[];
}

const mergeUserAppliedTags = (
  serverAppliedTags: IUserAppliedTagList[],
  localAppliedTags: IUserAppliedTagList[]
) => {
  if (!localAppliedTags.length) return serverAppliedTags;
  if (!serverAppliedTags.length) return localAppliedTags;

  const localTagsByFieldId = new Map(
    localAppliedTags.map((item) => [item.tagField.fieldId, item])
  );
  const serverFieldIds = new Set(
    serverAppliedTags.map((item) => item.tagField.fieldId)
  );

  return [
    ...serverAppliedTags.map(
      (item) => localTagsByFieldId.get(item.tagField.fieldId) ?? item
    ),
    ...localAppliedTags.filter(
      (item) => !serverFieldIds.has(item.tagField.fieldId)
    ),
  ];
};

const TaggingPanel = ({
  activeDocumentId,
  searchId,
  indexName,
  stdIndexFieldTemplateId,
  isExpanded = true,
}: ITaggingPannelProps) => {
  const [localUserAppliedTags, setLocalUserAppliedTags] =
    useState<LocalUserAppliedTagsState>({
      scopeKey: '',
      tags: [],
    });

  const {
    getFields,
    fields,
    isLoading: isFieldsLoading,
    isFailedToLoadContent: isFieldsLoadFailed,
  } = useGetEcaFields();

  const {
    getAllSelectedTags,
    selectedFieldsAndTags,
    isLoading: isSelectedTagsLoading,
    isFailedToLoadContent: isTagsLoadFailed,
    selectedTagsScope,
    invalidateSelectedTags,
  } = useGetSelectedTags();

  // Showing Mock data for export tags reverted 9605
  const exportTagList = useMemo<IFieldModal[]>(() => {
    return tagFieldmockData.slice(0, 1).map((field) => ({
      ...field,
      tagValue: field.tagValues.slice(0, 2),
    }));
  }, []);

  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;
  const {projectId} = useSelector((state: RootState) => state.reports);
  const [fieldTemplateID, setFieldTemplateID] = useState<number | undefined>(
    undefined
  );
  const indexListRequestId = useRef(0);

  const location = useLocation();
  const locationQueryParam = new URLSearchParams(location.search);
  const currentIndex = locationQueryParam.get('indexName');

  const getIndexList = useCallback(
    async (clientId: string, projectId: string) => {
      const currentRequestId = ++indexListRequestId.current;
      try {
        const response = await getIndexByClientIdAndProjectId(
          clientId,
          projectId
        );
        if (currentRequestId !== indexListRequestId.current) return;

        if (response?.status === 200) {
          const indexData = response?.data?.data ?? [];
          const standardIndex = indexData.find(
            (item: {name?: string; fieldTemplateId?: number}) =>
              item?.name === 'Standard Index'
          );

          setFieldTemplateID(
            standardIndex?.fieldTemplateId ?? stdIndexFieldTemplateId
          );
        }
      } catch (error) {
        console.error('Error fetching index list:', error);
      }
    },
    [stdIndexFieldTemplateId]
  );

  useEffect(() => {
    if (!isExpanded) return;

    void getFields();
  }, [getFields, isExpanded]);

  useEffect(() => {
    if (!isExpanded) return;

    if (fieldTemplateID) {
      void getAllSelectedTags(activeDocumentId ?? '', fieldTemplateID);
    }
  }, [activeDocumentId, fieldTemplateID, getAllSelectedTags, isExpanded]);

  useEffect(() => {
    if (!isExpanded) return;

    if (currentIndex && currentIndex !== 'Standard Index') {
      void getIndexList(clientId, projectId ?? '');
    } else {
      indexListRequestId.current += 1;
      setFieldTemplateID(stdIndexFieldTemplateId);
    }
  }, [
    clientId,
    currentIndex,
    getIndexList,
    isExpanded,
    projectId,
    stdIndexFieldTemplateId,
  ]);

  const taggingScopeKey = useMemo(
    () =>
      JSON.stringify([
        clientId,
        projectId,
        activeDocumentId,
        fieldTemplateID,
      ]),
    [activeDocumentId, clientId, fieldTemplateID, projectId]
  );
  const currentTaggingScopeKeyRef = useRef(taggingScopeKey);
  currentTaggingScopeKeyRef.current = taggingScopeKey;

  const invalidateCurrentSelectedTags = useCallback(() => {
    invalidateSelectedTags?.(activeDocumentId, fieldTemplateID);
  }, [activeDocumentId, fieldTemplateID, invalidateSelectedTags]);

  const previousTaggingScopeKey = useRef(taggingScopeKey);
  useEffect(() => {
    if (previousTaggingScopeKey.current === taggingScopeKey) return;

    previousTaggingScopeKey.current = taggingScopeKey;
    setLocalUserAppliedTags({
      scopeKey: taggingScopeKey,
      tags: [],
    });
  }, [taggingScopeKey]);

  const selectedTagsMatchCurrentScope =
    selectedTagsScope === undefined ||
    (selectedTagsScope !== null &&
      selectedTagsScope.documentId === activeDocumentId &&
      selectedTagsScope.fieldTemplateId === fieldTemplateID);

  const serverAppliedTags = useMemo<IUserAppliedTagList[]>(() => {
    if (
      !selectedTagsMatchCurrentScope ||
      !fields.length ||
      !selectedFieldsAndTags.length
    ) {
      return [];
    }

    return selectedFieldsAndTags.flatMap((item) => {
      const sourceTagField = fields.find(
        (field) => field.fieldId === Number(item.fieldId)
      );
      if (!sourceTagField) return [];

      const sortedFieldValues = (item.fieldValues ?? [])
        .slice()
        .sort((a, b) => b.displayOrder - a.displayOrder);
      const allTags: ITag[] = sortedFieldValues.map((value) => ({
        tagValueId: Number(value.fieldValueId),
        tagValue: value.fieldValue,
        displayOrder: value.displayOrder,
      }));

      return [
        {
          tagField: {
            ...sourceTagField,
            tagValues: allTags,
          },
          selectedTags: allTags.filter(
            (_tag, index) => sortedFieldValues[index].selected
          ),
        },
      ];
    });
  }, [fields, selectedFieldsAndTags, selectedTagsMatchCurrentScope]);

  const scopedLocalUserAppliedTags =
    localUserAppliedTags.scopeKey === taggingScopeKey
      ? localUserAppliedTags.tags
      : [];

  const setUserSelectedTags = useCallback<
    Dispatch<SetStateAction<IUserAppliedTagList[]>>
  >(
    (update) => {
      setLocalUserAppliedTags((previousState) => {
        if (currentTaggingScopeKeyRef.current !== taggingScopeKey) {
          return previousState;
        }

        const currentLocalTags =
          previousState.scopeKey === taggingScopeKey
            ? previousState.tags
            : [];
        const currentTags = mergeUserAppliedTags(
          serverAppliedTags,
          currentLocalTags
        );
        const nextTags =
          typeof update === 'function' ? update(currentTags) : update;

        return {
          scopeKey: taggingScopeKey,
          tags: nextTags,
        };
      });
    },
    [serverAppliedTags, taggingScopeKey]
  );

  const userSelectedTags = useMemo(() => {
    return mergeUserAppliedTags(serverAppliedTags, scopedLocalUserAppliedTags);
  }, [scopedLocalUserAppliedTags, serverAppliedTags]);

  const onApplyTagField = useCallback(
    (newTagField: IFieldModal, selectedTags: ITag[]) => {
      invalidateCurrentSelectedTags();
      setUserSelectedTags((prev) => [
        ...prev,
        {tagField: newTagField, selectedTags},
      ]);
    },
    [invalidateCurrentSelectedTags, setUserSelectedTags]
  );

  const renderLoader = () => (
    <Box className="tagging-panel-loader">
      <AurLoadingIndicator show={true} />
    </Box>
  );

  const renderErrorOverlay = () => (
    <Box className="content-loading-error">
      <AurDataGridErrorOverlay
        onRefresh={() => {
          if (isFieldsLoadFailed) getFields();
          if (isTagsLoadFailed)
            getAllSelectedTags(activeDocumentId ?? '', fieldTemplateID);
        }}
        enableParentWrapper={false}
      />
    </Box>
  );

  const renderMainContent = () => (
    <>
      <UserAppliedTag
        userAppliedTagList={userSelectedTags}
        setUserAppliedTagsList={setUserSelectedTags}
        activeDocumentId={activeDocumentId}
        indexName={indexName}
        onMutationSuccess={invalidateCurrentSelectedTags}
      />
      <SelectFieldAndApplyTag
        tagsFieldList={fields.filter(
          (tagField) =>
            !userSelectedTags
              .map((field) => `${field.tagField.fieldId}`)
              .includes(`${tagField.fieldId}`)
        )}
        onApplyTagField={onApplyTagField}
        activeDocumentId={activeDocumentId}
        searchId={searchId}
        indexName={indexName}
      />
      {/* Reverting the change of ARHD-9605 */}
      <ExportTag exportTagList={exportTagList} />
    </>
  );

  const renderContent = () => {
    if (isFieldsLoadFailed || isTagsLoadFailed) {
      return renderErrorOverlay();
    } else {
      return renderMainContent();
    }
  };

  return (
    <Box className="tagging-panel" data-testid="tagging-panel">
      {isFieldsLoading || isSelectedTagsLoading
        ? renderLoader()
        : renderContent()}
    </Box>
  );
};

export default TaggingPanel;
